import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataGrid } from 'react-data-grid';
import './spreadsheet.css';
import Papa from 'papaparse';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Download, Upload, ChevronDown, ArrowLeft, ArrowRight, EyeOff, Eye, Pencil, Palette, X } from 'lucide-react';
import { workspaceApi } from '../../lib/workspaceApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';

export const COLUMN_TYPES = [
  { id: 'text', label: 'Text' },
  { id: 'number', label: 'Number' },
  { id: 'date', label: 'Date' },
  { id: 'select', label: 'Dropdown' },
  { id: 'checkbox', label: 'Checkbox' },
];

export const PALETTE = [
  { label: 'No fill', value: '' },
  { label: 'Yellow', value: '#FDE68A' },
  { label: 'Green', value: '#A7F3D0' },
  { label: 'Blue', value: '#BFDBFE' },
  { label: 'Purple', value: '#DDD6FE' },
  { label: 'Pink', value: '#FBCFE8' },
  { label: 'Red', value: '#FECACA' },
  { label: 'Orange', value: '#FED7AA' },
  { label: 'Teal', value: '#99F6E4' },
  { label: 'Grey', value: '#E2E8F0' },
];

const emptyDraft = { id: null, name: '', type: 'text', choices: [{ label: '', color: '' }], color: '', position: null };
const TRUE_VALUES = new Set(['true', 'TRUE', '1', 'yes', 'Yes']);

const ColorPicker = ({ value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {PALETTE.map(swatch => (
      <button
        key={swatch.value || 'none'}
        type="button"
        title={swatch.label}
        data-testid={`${testid}-${swatch.value ? swatch.value.replace('#', '') : 'none'}`}
        onClick={() => onChange(swatch.value)}
        className={`h-7 w-7 rounded-md border flex items-center justify-center transition-all ${value === swatch.value ? 'ring-2 ring-offset-1 ring-slate-500' : 'border-slate-200'}`}
        style={{ backgroundColor: swatch.value || '#fff' }}
      >
        {!swatch.value && <X className="h-3 w-3 text-slate-400" />}
      </button>
    ))}
  </div>
);

export const FreeSheetGrid = ({ sheet, standalone = false }) => {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [mobileColumn, setMobileColumn] = useState('');
  const [cursor, setCursor] = useState(null);
  const [fillOpen, setFillOpen] = useState(false);
  const csvInput = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await workspaceApi(`/free-sheets/${sheet.id}`);
      setColumns(data.columns || []);
      setRows(data.rows || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [sheet.id]);

  useEffect(() => { load(); setCursor(null); }, [load]);

  const visible = useMemo(() => columns.filter(column => showHidden || !column.hidden), [columns, showHidden]);
  const hiddenCount = columns.filter(column => column.hidden).length;
  useEffect(() => { if (!visible.some(column => column.id === mobileColumn)) setMobileColumn(visible[0]?.id || ''); }, [visible, mobileColumn]);

  const cellValue = (row, columnId) => String(row.values?.[columnId] ?? '');
  const fillOf = (row, column) => row.styles?.[column.id] || (column.type === 'select' ? column.option_colors?.[cellValue(row, column.id)] || '' : '');

  const saveCells = async changes => {
    if (!changes.length) return;
    setBusy(true); setError('');
    setRows(previous => previous.map(row => {
      const own = changes.filter(change => change.id === row.id);
      return own.length ? { ...row, values: { ...row.values, ...Object.fromEntries(own.map(change => [change.field, change.value])) } } : row;
    }));
    try { await workspaceApi(`/free-sheets/${sheet.id}/cells`, { method: 'PATCH', body: JSON.stringify({ changes }) }); }
    catch (e) { setError(e.message); await load(); } finally { setBusy(false); }
  };

  const fillCell = async color => {
    if (!cursor) { toast.error('Click the cell you want to colour first'); return; }
    setFillOpen(false);
    setRows(previous => previous.map(row => row.id === cursor.rowId
      ? { ...row, styles: { ...(row.styles || {}), [cursor.columnId]: color } } : row));
    setBusy(true);
    try { await workspaceApi(`/free-sheets/${sheet.id}/styles`, { method: 'PATCH', body: JSON.stringify({ changes: [{ id: cursor.rowId, field: cursor.columnId, color }] }) }); }
    catch (e) { toast.error(e.message); await load(); } finally { setBusy(false); }
  };

  const addRows = async (payload = { count: 1 }) => {
    setBusy(true); setError('');
    try {
      const data = await workspaceApi(`/free-sheets/${sheet.id}/rows`, { method: 'POST', body: JSON.stringify(payload) });
      setRows(previous => [...previous, ...(data.rows || [])]);
      return data.rows || [];
    } catch (e) { setError(e.message); toast.error(e.message); return []; } finally { setBusy(false); }
  };

  const deleteRow = async rowId => {
    setBusy(true);
    try { await workspaceApi(`/free-sheets/${sheet.id}/rows/${rowId}`, { method: 'DELETE' }); setRows(previous => previous.filter(row => row.id !== rowId)); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const applyColumns = data => setColumns(data.columns || []);

  const saveColumn = async () => {
    const name = draft.name.trim();
    if (!name) { toast.error('Enter a column name'); return; }
    const choices = draft.type === 'select' ? draft.choices.filter(choice => choice.label.trim()) : [];
    const body = {
      name, type: draft.type, color: draft.color || '',
      options: choices.map(choice => choice.label.trim()),
      option_colors: Object.fromEntries(choices.filter(choice => choice.color).map(choice => [choice.label.trim(), choice.color])),
    };
    setBusy(true);
    try {
      if (draft.id) applyColumns(await workspaceApi(`/free-sheets/${sheet.id}/columns/${draft.id}`, { method: 'PATCH', body: JSON.stringify(body) }));
      else applyColumns(await workspaceApi(`/free-sheets/${sheet.id}/columns`, { method: 'POST', body: JSON.stringify({ ...body, position: draft.position }) }));
      setDraft(null);
      toast.success(draft.id ? 'Column updated' : 'Column added');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const patchColumn = async (columnId, body) => {
    setBusy(true);
    try { applyColumns(await workspaceApi(`/free-sheets/${sheet.id}/columns/${columnId}`, { method: 'PATCH', body: JSON.stringify(body) })); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const deleteColumn = async columnId => {
    if (!window.confirm('Delete this column and the values inside it?')) return;
    setBusy(true);
    try { applyColumns(await workspaceApi(`/free-sheets/${sheet.id}/columns/${columnId}`, { method: 'DELETE' })); await load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const moveColumn = async (columnId, offset) => {
    const order = columns.map(column => column.id);
    const index = order.indexOf(columnId);
    const target = index + offset;
    if (index < 0 || target < 0 || target >= order.length) return;
    order.splice(target, 0, order.splice(index, 1)[0]);
    setBusy(true);
    try { applyColumns(await workspaceApi(`/free-sheets/${sheet.id}/columns/order`, { method: 'POST', body: JSON.stringify({ column_ids: order }) })); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const editDraftFor = column => setDraft({
    id: column.id, name: column.name, type: column.type, color: column.color || '', position: null,
    choices: (column.options || []).length
      ? (column.options || []).map(option => ({ label: option, color: column.option_colors?.[option] || '' }))
      : [{ label: '', color: '' }],
  });

  const pasteBlock = async (startRowIndex, startColIndex, values) => {
    const needed = startRowIndex + values.length - rows.length;
    let sheetRows = rows;
    if (needed > 0) {
      const created = await addRows({ count: needed });
      if (created.length < needed) return;
      sheetRows = [...rows, ...created];
    }
    const changes = [];
    values.forEach((line, y) => line.forEach((value, x) => {
      const column = visible[startColIndex + x];
      const row = sheetRows[startRowIndex + y];
      if (column && row) changes.push({ id: row.id, field: column.id, value: String(value ?? '') });
    }));
    if (!changes.length) return;
    await saveCells(changes);
    if (needed > 0) await load();
  };

  const importCsv = async file => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('CSV must be 2 MB or smaller'); return; }
    const parsed = Papa.parse(await file.text(), { header: true, skipEmptyLines: 'greedy' });
    if (parsed.errors.length) { toast.error(parsed.errors[0].message); return; }
    const headers = (parsed.meta.fields || []).map(header => header.trim()).filter(Boolean);
    if (!headers.length || !parsed.data.length) { toast.error('That CSV has no columns or rows'); return; }
    if (parsed.data.length > 2000) { toast.error('Import up to 2,000 rows at a time'); return; }
    setBusy(true);
    try {
      let current = columns;
      for (const header of headers) {
        if (!current.some(column => column.name.toLowerCase() === header.toLowerCase())) {
          const data = await workspaceApi(`/free-sheets/${sheet.id}/columns`, { method: 'POST', body: JSON.stringify({ name: header, type: 'text' }) });
          current = data.columns;
        }
      }
      setColumns(current);
      const byName = Object.fromEntries(current.map(column => [column.name.toLowerCase(), column.id]));
      const payload = parsed.data.map(line => Object.fromEntries(Object.entries(line)
        .filter(([key]) => byName[key.trim().toLowerCase()])
        .map(([key, value]) => [byName[key.trim().toLowerCase()], String(value ?? '')])));
      await workspaceApi(`/free-sheets/${sheet.id}/rows`, { method: 'POST', body: JSON.stringify({ rows: payload }) });
      await load();
      toast.success(`Imported ${payload.length} rows`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const exportCsv = () => {
    const csv = Papa.unparse({ fields: visible.map(column => column.name), data: rows.map(row => visible.map(column => cellValue(row, column.id))) }, { escapeFormulae: true });
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url; link.download = `${sheet.name}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  const headerMenu = column => (
    <div className="flex items-center justify-between gap-1 w-full" style={column.color ? { backgroundColor: column.color, margin: '0 -10px', padding: '0 10px' } : undefined}>
      <span className="truncate text-xs font-semibold text-slate-700">{column.name}{column.hidden ? ' (hidden)' : ''}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`free-column-menu-${column.id}`} className="p-0.5 rounded hover:bg-slate-200" onClick={event => event.stopPropagation()}>
            <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem data-testid={`free-column-rename-${column.id}`} onClick={() => editDraftFor(column)}>
            <Pencil className="h-4 w-4 mr-2" />Rename / type / colour
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`free-column-insert-${column.id}`} onClick={() => setDraft({ ...emptyDraft, choices: [{ label: '', color: '' }], position: columns.findIndex(item => item.id === column.id) + 1 })}>
            <Plus className="h-4 w-4 mr-2" />Insert column right
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`free-column-left-${column.id}`} onClick={() => moveColumn(column.id, -1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />Move left
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`free-column-right-${column.id}`} onClick={() => moveColumn(column.id, 1)}>
            <ArrowRight className="h-4 w-4 mr-2" />Move right
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`free-column-hide-${column.id}`} onClick={() => patchColumn(column.id, { hidden: !column.hidden })}>
            {column.hidden ? <><Eye className="h-4 w-4 mr-2" />Unhide</> : <><EyeOff className="h-4 w-4 mr-2" />Hide</>}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid={`free-column-delete-${column.id}`} className="text-red-600" onClick={() => deleteColumn(column.id)}>
            <Trash2 className="h-4 w-4 mr-2" />Delete column
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const gridColumns = [
    { key: '__index', name: '#', width: 56, frozen: true, renderCell: ({ rowIdx }) => <span className="text-xs text-slate-400">{rowIdx + 1}</span> },
    ...visible.map(column => ({
      key: column.id,
      name: column.name,
      width: column.width || 160,
      minWidth: 80,
      resizable: true,
      editable: !busy && column.type !== 'checkbox',
      renderHeaderCell: () => headerMenu(column),
      renderCell: ({ row }) => {
        const value = cellValue(row, column.id);
        const fill = fillOf(row, column);
        if (column.type === 'checkbox') return (
          <span className="free-fill flex items-center" style={fill ? { backgroundColor: fill } : undefined}>
            <input type="checkbox" data-testid={`free-check-${row.id}-${column.id}`} checked={TRUE_VALUES.has(value)}
              onChange={event => saveCells([{ id: row.id, field: column.id, value: event.target.checked ? 'TRUE' : '' }])} />
          </span>
        );
        return (
          <span className="free-fill" style={fill ? { backgroundColor: fill } : undefined} data-testid={`free-cell-${row.id}-${column.id}`}>{value}</span>
        );
      },
      renderEditCell: ({ row, onRowChange, onClose }) => column.type === 'select' ? (
        <select autoFocus data-testid={`free-editor-${row.id}-${column.id}`} className="w-full h-full border-2 border-emerald-600 px-2 bg-white text-sm outline-none"
          value={cellValue(row, column.id)}
          onChange={event => onRowChange({ ...row, values: { ...row.values, [column.id]: event.target.value } }, true)}
          onBlur={() => onClose(true, false)}>
          <option value="">—</option>
          {(column.options || []).map(option => <option key={option} value={option}>{option}</option>)}
        </select>
      ) : (
        <input autoFocus data-testid={`free-editor-${row.id}-${column.id}`}
          type={column.type === 'number' ? 'number' : column.type === 'date' ? 'date' : 'text'}
          className="w-full h-full border-2 border-emerald-600 px-2 bg-white text-sm outline-none"
          value={cellValue(row, column.id)}
          onChange={event => onRowChange({ ...row, values: { ...row.values, [column.id]: event.target.value } })}
          onBlur={() => onClose(true, false)} />
      ),
    })),
    { key: '__actions', name: '', width: 52, renderCell: ({ row }) => (
      <button data-testid={`free-row-delete-${row.id}`} className="text-slate-400 hover:text-red-600" title="Delete row" onClick={() => deleteRow(row.id)}>
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    ) },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>;

  return (
    <div data-testid="free-sheet-workspace" className="min-w-0 w-full space-y-2">
      <div className="flex flex-wrap items-center gap-2 px-3 pt-3">
        <Button data-testid="free-add-column" size="sm" onClick={() => setDraft({ ...emptyDraft, choices: [{ label: '', color: '' }] })} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-1" />Add column
        </Button>
        <Button data-testid="free-add-row" size="sm" variant="outline" disabled={busy || !columns.length} onClick={() => addRows({ count: 1 })}>
          <Plus className="h-4 w-4 mr-1" />Add row
        </Button>
        <Button data-testid="free-add-rows-10" size="sm" variant="outline" disabled={busy || !columns.length} onClick={() => addRows({ count: 10 })}>+10 rows</Button>
        <DropdownMenu open={fillOpen} onOpenChange={setFillOpen}>
          <DropdownMenuTrigger asChild>
            <Button data-testid="free-fill-colour" size="sm" variant="outline" disabled={busy}>
              <Palette className="h-4 w-4 mr-1" />Fill colour
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="p-3 w-auto">
            <p className="text-xs text-slate-500 mb-2">{cursor ? 'Colour the selected cell' : 'Click a cell first, then pick a colour'}</p>
            <ColorPicker testid="free-fill-palette" value="" onChange={fillCell} />
          </DropdownMenuContent>
        </DropdownMenu>
        <input ref={csvInput} data-testid="free-csv-file" type="file" accept=".csv,text/csv" hidden onChange={event => { importCsv(event.target.files[0]); event.target.value = ''; }} />
        <Button data-testid="free-import-csv" size="sm" variant="outline" disabled={busy} onClick={() => csvInput.current.click()}>
          <Upload className="h-4 w-4 mr-1" />Import CSV
        </Button>
        <Button data-testid="free-export-csv" size="sm" variant="outline" onClick={exportCsv}>
          <Download className="h-4 w-4 mr-1" />Export
        </Button>
        {hiddenCount > 0 && (
          <Button data-testid="free-toggle-hidden" size="sm" variant="outline" onClick={() => setShowHidden(value => !value)}>
            {showHidden ? 'Hide hidden columns' : `Show ${hiddenCount} hidden column(s)`}
          </Button>
        )}
      </div>

      <p data-testid="free-save-status" className="text-xs text-slate-500 px-3">
        {busy ? 'Saving changes…' : error ? 'Some changes were not saved' : 'All saved'} · {rows.length} rows · {visible.length} columns
      </p>
      {error && <p data-testid="free-grid-error" role="alert" className="text-sm text-red-700 px-3">{error}</p>}

      {!columns.length ? (
        <div data-testid="free-no-columns" className="px-5 py-12 text-center border-t bg-gradient-to-b from-emerald-50/50 to-white">
          <h2 className="text-lg font-semibold text-slate-800">Add your first column</h2>
          <p className="text-sm text-slate-500 mt-2">This blank sheet has no columns yet — name them anything you like.</p>
        </div>
      ) : (
        <>
          <div className="hidden md:block min-w-0">
            <DataGrid className="officium-data-grid" data-testid="free-data-grid" rowHeight={36} headerRowHeight={38}
              style={{ height: standalone ? 'calc(100vh - 270px)' : Math.min(620, 80 + rows.length * 36), maxHeight: standalone ? 'none' : '65vh', minHeight: 200, width: '100%' }}
              columns={gridColumns} rows={rows} rowKeyGetter={row => row.id}
              onCellClick={({ row, column }) => { if (row && column && !column.key.startsWith('__')) setCursor({ rowId: row.id, columnId: column.key }); }}
              onRowsChange={(updated, { indexes, column }) => saveCells(indexes
                .filter(index => cellValue(updated[index], column.key) !== cellValue(rows[index], column.key))
                .map(index => ({ id: rows[index].id, field: column.key, value: cellValue(updated[index], column.key) })))}
              onCellCopy={(args, event) => { event.preventDefault(); event.clipboardData.setData('text/plain', cellValue(args.row, args.column.key)); }}
              onCellPaste={(args, event) => {
                event.preventDefault();
                const values = Papa.parse(event.clipboardData.getData('text/plain'), { delimiter: '\t', skipEmptyLines: 'greedy' }).data;
                const startRow = rows.findIndex(row => row.id === args.row.id);
                const startCol = visible.findIndex(column => column.id === args.column.key);
                if (startRow >= 0 && startCol >= 0) pasteBlock(startRow, startCol, values);
                return args.row;
              }} />
          </div>

          <div className="md:hidden space-y-3 p-3">
            <select data-testid="free-mobile-column" aria-label="Column to edit" className="border rounded p-2 w-full" value={mobileColumn} onChange={event => setMobileColumn(event.target.value)}>
              {visible.map(column => <option key={column.id} value={column.id}>{column.name}</option>)}
            </select>
            {rows.map((row, index) => {
              const column = visible.find(item => item.id === mobileColumn);
              return (
                <div key={row.id} className="border rounded-lg p-3 min-w-0" style={fillOf(row, column || {}) ? { backgroundColor: fillOf(row, column) } : undefined}>
                  <p className="text-xs text-slate-500 mb-2">Row {index + 1}</p>
                  {column?.type === 'select' ? (
                    <select data-testid={`free-mobile-cell-${row.id}`} className="border rounded p-2 w-full bg-white" value={cellValue(row, mobileColumn)}
                      onChange={event => saveCells([{ id: row.id, field: mobileColumn, value: event.target.value }])}>
                      <option value="">—</option>
                      {(column.options || []).map(option => <option key={option} value={option}>{option}</option>)}
                    </select>
                  ) : column?.type === 'checkbox' ? (
                    <input type="checkbox" data-testid={`free-mobile-cell-${row.id}`} checked={TRUE_VALUES.has(cellValue(row, mobileColumn))}
                      onChange={event => saveCells([{ id: row.id, field: mobileColumn, value: event.target.checked ? 'TRUE' : '' }])} />
                  ) : (
                    <Input key={`${row.id}-${mobileColumn}-${cellValue(row, mobileColumn)}`} data-testid={`free-mobile-cell-${row.id}`} disabled={busy}
                      defaultValue={cellValue(row, mobileColumn)}
                      onBlur={event => { if (event.target.value !== cellValue(row, mobileColumn)) saveCells([{ id: row.id, field: mobileColumn, value: event.target.value }]); }} />
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <Dialog open={!!draft} onOpenChange={open => { if (!open && !busy) setDraft(null); }}>
        <DialogContent data-testid="free-column-dialog" className="w-[calc(100%-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{draft?.id ? 'Edit column' : 'Add column'}</DialogTitle>
            <DialogDescription>Name it anything, pick a type, and colour it like a spreadsheet.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Column name</Label>
              <Input data-testid="free-column-name" autoFocus placeholder="e.g., Client, Invoice No, Interview Date" value={draft?.name || ''}
                onChange={event => setDraft(previous => ({ ...previous, name: event.target.value }))} className="mt-1.5" />
            </div>
            <div>
              <Label>Type</Label>
              <select data-testid="free-column-type" className="mt-1.5 w-full border rounded-md p-2 text-sm" value={draft?.type || 'text'}
                onChange={event => setDraft(previous => ({ ...previous, type: event.target.value }))}>
                {COLUMN_TYPES.map(type => <option key={type.id} value={type.id}>{type.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Header colour</Label>
              <div className="mt-1.5">
                <ColorPicker testid="free-column-colour" value={draft?.color || ''} onChange={color => setDraft(previous => ({ ...previous, color }))} />
              </div>
            </div>
            {draft?.type === 'select' && (
              <div className="space-y-3">
                <Label>Dropdown choices and their colours</Label>
                {(draft.choices || []).map((choice, index) => (
                  <div key={index} className="rounded-lg border border-slate-200 p-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <Input data-testid={`free-choice-label-${index}`} placeholder={`Choice ${index + 1}`} value={choice.label}
                        onChange={event => setDraft(previous => ({ ...previous, choices: previous.choices.map((item, position) => position === index ? { ...item, label: event.target.value } : item) }))} />
                      <button type="button" data-testid={`free-choice-remove-${index}`} className="text-slate-400 hover:text-red-600"
                        onClick={() => setDraft(previous => ({ ...previous, choices: previous.choices.filter((item, position) => position !== index) }))}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <ColorPicker testid={`free-choice-colour-${index}`} value={choice.color}
                      onChange={color => setDraft(previous => ({ ...previous, choices: previous.choices.map((item, position) => position === index ? { ...item, color } : item) }))} />
                  </div>
                ))}
                <Button type="button" data-testid="free-choice-add" size="sm" variant="outline"
                  onClick={() => setDraft(previous => ({ ...previous, choices: [...previous.choices, { label: '', color: '' }] }))}>
                  <Plus className="h-4 w-4 mr-1" />Add choice
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="free-column-cancel" variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
            <Button data-testid="free-column-save" disabled={busy || !draft?.name?.trim()} onClick={saveColumn} className="bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              {draft?.id ? 'Save column' : 'Add column'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
